/**
 * @author: lv.chen
 * @date: 2025/8/8
 * @desc: ReportUploader组件，内置PDF报告上传COS、AI识别报告功能
 */
import React, { useState, useEffect } from 'react';
import { history, useIntl } from 'umi';
import {
  Modal,
  // Button,
  Progress,
  notification,
} from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import Uploader from '@home/components/Uploader';

import './index.scss';

import IMG_AI_TEXT from '@home/assets/img/ai-text.svg';
import FileUploadImage from '@/pages/aigc/assets/svg/file-upload.svg';

/**
 * 报告上传步骤
 */
enum ReportUploadStep {
  /**
   * 选择报告文件
   */
  SelectFile = 0,
  /**
   * AI识别报告
   */
  UploadAndAIRecognize,
}

// 允许上传的报告文件格式
const ACCEPT_MIMETYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/mspowerpoint',
  'application/powerpoint',
  'application/x-mspowerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/pdf',
  'text/html',
  'application/zip',
  'application/x-zip-compressed',
  '.html',
  '.htm',
  '.zip',
];

const REPORT_AI_RECOGNIZE_MAX_SIZE = 15 * 1024 * 1024;

const getIsHTMLFile = (file: File): boolean => {
  const fileName = file?.name?.toLowerCase() || '';
  return file?.type === 'text/html' || fileName.endsWith('.html') || fileName.endsWith('.htm');
};

const getIsZipFile = (file: File): boolean => {
  const fileName = file?.name?.toLowerCase() || '';
  return file?.type === 'application/zip' || file?.type === 'application/x-zip-compressed' || fileName.endsWith('.zip');
};

const shouldRecognizeReport = (file: File): boolean => {
  if (file?.size > REPORT_AI_RECOGNIZE_MAX_SIZE) {
    return false;
  }
  if (getIsHTMLFile(file) || getIsZipFile(file)) {
    return false;
  }
  return true;
};

interface IReportUploaderProps extends IBaseProps {
  cancelable?: boolean;
  showLinker?: boolean;
  showNotificationWhenUploadHTMLOrZipFile?: boolean;
  recognize: (file: File, options: { onProgress: (progress: { percent: number }) => void }) => Promise<any>;
  onRecognized?: (file: File, result: IRecord | null) => void;
  onRecognitionError?: (file: File, error: unknown) => void;
  button: React.ReactElement;
  triggerOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ReportUploader: React.FC<IReportUploaderProps> = (props) => {
  const {
    cancelable = false,
    showLinker = false,
    showNotificationWhenUploadHTMLOrZipFile = false,
    recognize,
    onRecognized,
    onRecognitionError,
    button,
    triggerOpen,
    onOpenChange,
  } = props;

  const showFooter = showLinker;

  const { formatMessage } = useIntl();

  const [visible, setVisible] = useState(false);

  const [step, setStep] = useState<ReportUploadStep>(ReportUploadStep.SelectFile);

  const [progress, setProgress] = useState(0);

  const percent = Math.round(progress * 100);

  // Handle external trigger to open modal
  useEffect(() => {
    if (triggerOpen && !visible) {
      setVisible(true);
      onOpenChange?.(true);
    }
  }, [triggerOpen]);

  // Notify parent when visibility changes
  useEffect(() => {
    if (!visible && onOpenChange) {
      onOpenChange(false);
    }
  }, [visible, onOpenChange]);

  const handleUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }

    if (!shouldRecognizeReport(file)) {
      setStep(ReportUploadStep.SelectFile);
      setProgress(0);
      setVisible(false);
      if (showNotificationWhenUploadHTMLOrZipFile) {
        notification.warning({
          unique: true,
          duration: 3000,
          description: formatMessage({ id: 'dbh_report_uploader_ai_unsupported_tips' }),
        });
      }
      onRecognized?.(file, null);
      return;
    }

    try {
      setStep(ReportUploadStep.UploadAndAIRecognize);
      const recognizedResult = await recognize(file, {
        onProgress: (progress) => {
          setProgress(progress?.percent ?? 0);
        },
      });

      if (recognizedResult?.data?.result === 'error') {
        throw new Error('AI report recognition returned an error result.');
      }

      setStep(ReportUploadStep.SelectFile);
      setProgress(1);
      setVisible(false);

      onRecognized?.(file, recognizedResult?.data);
    } catch (error) {
      console.error(error);
      setStep(ReportUploadStep.SelectFile);
      setProgress(0);
      setVisible(false);
      onRecognized?.(file, null);
      onRecognitionError?.(file, error);
    }
  };

  return (
    <>
      <Modal
        className="dbh-report-uploader-modal"
        caption={formatMessage({ id: 'dbu_report_uploader_title' })}
        disableCloseIcon={step === ReportUploadStep.UploadAndAIRecognize && !cancelable}
        visible={visible}
        onClose={() => {
          setVisible(false);
        }}
      >
        <Modal.Body>
          {step === ReportUploadStep.SelectFile && (
            <div className="dbu-report-uploader-step1">
              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <Uploader
                accept={ACCEPT_MIMETYPES}
                placeholder={
                  <div className="dbh-uploader__placeholder">
                    <img src={FileUploadImage} className="dbh-uploader__placeholder__icon" alt="" />
                    <div className="dbh-uploader__placeholder__info-list">
                      <div className="dbh-uploader__placeholder__info-item">
                        {formatMessage({ id: 'dbh_uploader_placeholder_text1' })}
                      </div>
                      <div className="dbh-uploader__placeholder__info-item">
                        {formatMessage({ id: 'dbh_report_uploader_placeholder_desc' })}
                      </div>
                      <div className="dbh-uploader__placeholder__info-item">
                        {formatMessage({ id: 'dbh_report_uploader_placeholder_html_zip_tips' })}
                      </div>
                    </div>
                  </div>
                }
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSelect={handleUpload}
              />
            </div>
          )}
          {step === ReportUploadStep.UploadAndAIRecognize && (
            <div className="dbu-report-uploader-step2">
              <div className="dbu-report-uploader-step2-tips">
                <img className="dbu-report-uploader-step2-tips-icon" src={IMG_AI_TEXT} alt="" />
                {formatMessage({ id: 'dbh_ai_recognize_tips' })}
              </div>
              <Progress className="dbu-report-uploader-step2-progress" percent={percent} />
              <div className="dbu-report-uploader-step2-progress-desc">
                <span>{formatMessage({ id: 'dbh_ai_recognize_estimated_time_text' }, { range: '1~2' })}</span>
                <span>{percent}%</span>
              </div>
              {/* <div className="dbu-report-uploader-step2-footer">*/}
              {/*  <Button*/}
              {/*    type="link"*/}
              {/*    onClick={(e) => {*/}
              {/*      e?.stopPropagation();*/}
              {/*      setStep(ReportUploadStep.SelectFile);*/}
              {/*      setProgress(0);*/}
              {/*      setVisible(false);*/}
              {/*    }}*/}
              {/*  >*/}
              {/*    Cancel Upload*/}
              {/*  </Button>*/}
              {/* </div>*/}
            </div>
          )}
          {showFooter && (
            <div className="dbu-report-uploader__footer">
              {showLinker && (
                <div
                  className="dbu-report-uploader__linker"
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push('/user-center/content-management-center/reports');
                  }}
                >
                  <span>{formatMessage({ id: 'dbh_report_uploader_footer_linker_text' })}</span>
                  <SvgIcon type="link-to" />
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
      {React.cloneElement(button, {
        onClick: (e: any) => {
          e?.stopPropagation();
          setVisible(true);
        },
      })}
    </>
  );
};

export default ReportUploader;
